import {
  Typography,
  TextField,
  Button,
  Stack,
  Box,
  Dialog,
  Checkbox,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Iconify from "../../../../components/iconify/Iconify";
import { LoadingButton } from "@mui/lab";
import { ChangeEvent, useEffect, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ProductService } from "../../../../api/product.service";
import { RHFTextField } from "../../../../components/hook-form";
import { useFormContext, Controller } from "react-hook-form";

const label = { inputProps: { "aria-label": "Checkbox demo" } };

const columns: GridColDef[] = [
  { field: "name", headerName: "Name", width: 150 },
  { field: "code", headerName: "Code", width: 100 },
  { field: "type", headerName: "Type", width: 150 },
  {
    field: "salePrice",
    headerName: "Sale Price",
    type: "number",
    width: 120,
  },
  { field: "status", headerName: "Status", width: 100 },
];

type Row = {
  id: number;
  name: string;
  code: string;
  type?: string;
  salePrice: string;
  status: string;
  category?: string;
  currentStock: number;
};

interface PageProps {
  specType: string;
  fields: any[];
  append: Function;
  remove: Function;
  disabled: boolean;
}

export default function FramesAndLenses({
  specType,
  fields,
  append,
  remove,
  disabled,
}: PageProps) {
  const { control, setValue } = useFormContext();
  const [formModal, setFormModal] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [frameAccordionExpanded, setFrameAccordionExpanded] = useState(true);
  const [lensAccordionExpanded, setLensAccordionExpanded] = useState(false);
  const [differentRL, setDifferentRL] = useState(false);
  const [frames, setFrames] = useState<Row[]>([]);
  const [lens, setLens] = useState<Row[]>([]);
  const [lensFilter, setLensFilter] = useState<Row[]>([]);
  const [chooseFieldIndex, setChooseFieldIndex] = useState<number | null>(null);

  const getAllProducts = async () => {
    const productService = new ProductService();
    try {
      const { data } = await productService.getAllProducts();

      const mappedProducts = data.map((product: any) => ({
        id: product.id,
        name: product.name,
        code: product.code,
        type: product.type,
        salePrice: product.salePrice,
        status: product.status,
        category: product.category,
        currentStock: product.currentStock,
      }));

      // For now, show all products in both frames and lens
      // You can modify this logic based on how you want to categorize products
      const productFrames = mappedProducts.filter(
        (product: any) =>
          product.category === "frames" ||
          product.name.toLowerCase().includes("frame") ||
          product.code.startsWith("F"),
      );

      const productLens = mappedProducts.filter(
        (product: any) =>
          product.category === "lens" ||
          product.name.toLowerCase().includes("lens") ||
          product.code.startsWith("L"),
      );

      // If no specific categorization found, show all products in both sections
      setFrames(productFrames.length > 0 ? productFrames : mappedProducts);

      if (specType) {
        const filteredLens = productLens.filter(
          (lens: any) => lens.type === specType,
        );
        setLens(filteredLens.length > 0 ? filteredLens : mappedProducts);
        setLensFilter(filteredLens.length > 0 ? filteredLens : mappedProducts);
      } else {
        setLens(productLens.length > 0 ? productLens : mappedProducts);
        setLensFilter(productLens.length > 0 ? productLens : mappedProducts);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleFrameAccordionChange = () => {
    setFrameAccordionExpanded(!frameAccordionExpanded);
    setLensAccordionExpanded(false);
  };

  const handleLensAccordionChange = () => {
    setLensAccordionExpanded(!lensAccordionExpanded);
    setFrameAccordionExpanded(false);
  };

  const handleOpenModal = () => {
    setFormModal(true);
  };

  const handleCloseModal = () => {
    setFormModal(false);
  };

  const handleAddFrame = () => {
    append({ id: "", name: "", type: "", price: "", category: "" });
  };

  const handleLensChange = (event: any) => {
    setFilterSearch(event);
    const inputValue = event.trim();
    const filteredLens = lens.filter(
      (row) =>
        row?.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
        row?.code?.toLowerCase().includes(inputValue.toLowerCase()),
    );
    setLensFilter(filteredLens);
  };

  const handleChangeSelection = (selection: any, type: string) => {
    if (chooseFieldIndex === null || selection.length === 0) return;

    const selectedItem =
      type === "lens"
        ? lens.find((x) => x.id === selection[0])
        : type === "frames"
          ? frames.find((x) => x.id === selection[0])
          : null;

    if (selectedItem) {
      const fieldsToUpdate = {
        id: selectedItem.id,
        name: selectedItem.name,
        type: selectedItem.type,
        price: selectedItem.salePrice, // Using salePrice instead of price
        category: selectedItem.category || type, // Set category based on selection type
      };

      Object.entries(fieldsToUpdate).forEach(([key, value]) => {
        setValue(`framesAndLenses.${chooseFieldIndex}.${key}`, value);
      });

      handleCloseModal();
    }
  };

  useEffect(() => {
    getAllProducts();
  }, [specType]);

  const handleRemove = (id: any) => {
    const index = fields.findIndex((field) => field.id === id);
    if (index !== -1) {
      remove(index);
    }
  };

  return (
    <Stack
      sx={{
        width: "100%",
      }}
      direction="column"
      p={1}
    >
      <Stack direction="row" justifyContent="space-between">
        <Box sx={{ display: "flex", alignItems: "end", width: "100%", mb: 2 }}>
          <Typography variant="h6">Frames & Lenses</Typography>

          <Box sx={{ flexGrow: 1, borderBottom: "1px solid #ccc", mb: 0 }} />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Add a new frame and lens">
              <span>
                <Button
                  variant="outlined"
                  sx={{ ml: 1, width: "80px" }}
                  startIcon={<Iconify icon="eva:plus-fill" sx={{ mr: 0 }} />}
                  onClick={handleAddFrame}
                >
                  Add
                </Button>
              </span>
            </Tooltip>

            {/* <Tooltip title="Repair an existing frame">
              <span>
                <Button
                  variant="outlined"
                  sx={{ mb: 1, width: "80px" }}
                  startIcon={<Iconify icon="mynaui:wrench" sx={{ mr: 0 }} />}
                  onClick={handleAddFrame}>
                  Repair
                </Button>
              </span>
            </Tooltip> */}
          </Box>
        </Box>
      </Stack>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {fields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginBottom: 3,
            }}
          >
            <Controller
              name={`framesAndLenses.${index}.name`}
              control={control}
              defaultValue={field.name}
              rules={{ required: "Name is required" }}
              render={({ field, fieldState }) => (
                <RHFTextField
                  label="Name"
                  disabled={disabled}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  {...field}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
            <Controller
              name={`framesAndLenses.${index}.type`}
              control={control}
              defaultValue={field.type}
              rules={{ required: "Type is required" }}
              render={({ field, fieldState }) => (
                <RHFTextField
                  label="Type"
                  sx={{ ml: 1 }}
                  disabled={disabled}
                  fullWidth
                  {...field}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
            <Controller
              name={`framesAndLenses.${index}.price`}
              control={control}
              defaultValue={field.price}
              rules={{
                required: "Price is required",
                pattern: {
                  value: /^[0-9]*\.?[0-9]*$/, // allows integers and decimals
                  message: "Only numbers are allowed",
                },
              }}
              render={({ field, fieldState }) => (
                <RHFTextField
                  label="Price"
                  sx={{ ml: 1 }}
                  fullWidth
                  {...field}
                  disabled={disabled}
                  InputLabelProps={{ shrink: true }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                  onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                    e.target.value = e.target.value.replace(/[^0-9.]/g, ""); // remove letters
                    field.onChange(e);
                  }}
                />
              )}
            />
            <Controller
              name={`framesAndLenses.${index}.category`}
              control={control}
              defaultValue={field.category}
              rules={{ required: "Category is required" }}
              render={({ field, fieldState }) => (
                <RHFTextField
                  label="Category"
                  sx={{ ml: 1 }}
                  disabled={disabled}
                  fullWidth
                  {...field}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />

            <LoadingButton
              variant="outlined"
              sx={{ ml: 1 }}
              onClick={() => {
                handleOpenModal();
                setChooseFieldIndex(index);
              }}
            >
              Choose
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              color="error"
              sx={{ ml: 1 }}
              onClick={() => handleRemove(field.id)}
            >
              Remove
            </LoadingButton>
          </Box>
        ))}
      </Box>

      <Dialog
        fullWidth
        maxWidth="lg"
        open={formModal}
        onClose={handleCloseModal}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography variant="h4" sx={{ mb: 2, textAlign: "center" }}>
            Select Product
          </Typography>

          <Accordion
            expanded={frameAccordionExpanded}
            onChange={handleFrameAccordionChange}
            defaultExpanded
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography variant="h5">
                Choose Frame ({frames.length} available)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div style={{ height: 400, width: "100%" }}>
                <DataGrid
                  rows={frames}
                  columns={columns}
                  onRowSelectionModelChange={(e) =>
                    handleChangeSelection(e, "frames")
                  }
                  pageSizeOptions={[5, 10, 25]}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 5 },
                    },
                  }}
                />
              </div>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={lensAccordionExpanded}
            onChange={handleLensAccordionChange}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2-content"
              id="panel2-header"
            >
              <Typography variant="h5">
                Choose Lens ({lensFilter.length} available)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                spacing={2}
                alignItems="center"
                direction={{
                  xs: "column",
                  md: "row",
                }}
                sx={{ mb: 2 }}
              >
                <TextField
                  fullWidth
                  placeholder="Search by Name or Code"
                  value={filterSearch}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handleLensChange(event.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify
                          icon="eva:search-fill"
                          sx={{ color: "text.disabled" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Box sx={{ mb: 2 }}>
                <Checkbox
                  {...label}
                  checked={differentRL}
                  onChange={() => setDifferentRL(!differentRL)}
                />
                Different R&L
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Right Eye
              </Typography>
              <div style={{ height: 400, width: "100%" }}>
                <DataGrid
                  rows={lensFilter}
                  columns={columns}
                  onRowSelectionModelChange={(e) =>
                    handleChangeSelection(e, "lens")
                  }
                  pageSizeOptions={[5, 10, 25]}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 5 },
                    },
                  }}
                />
              </div>

              {differentRL && (
                <>
                  <Typography variant="subtitle1" sx={{ marginTop: 2, mb: 1 }}>
                    Left Eye
                  </Typography>
                  <div style={{ height: 400, width: "100%" }}>
                    <DataGrid
                      rows={lensFilter}
                      columns={columns}
                      onRowSelectionModelChange={(e) =>
                        handleChangeSelection(e, "lens")
                      }
                      pageSizeOptions={[5, 10, 25]}
                      initialState={{
                        pagination: {
                          paginationModel: { page: 0, pageSize: 5 },
                        },
                      }}
                    />
                  </div>
                </>
              )}
            </AccordionDetails>
          </Accordion>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}
          >
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Stack>
  );
}
